import DocumentUploader from "./DocumentUploader";
import DocumentList from "./DocumentList";
import { useOasisBackend } from "../../hooks/useOasisBackend";
import { useEffect, useState } from "react";
import { ReadDatabase, Document } from "../../api/OasisBackendApi";
import TranslateDatabaseButton from "./TranslateDatabaseButton";
import EntityCollectionDetail from "./EntityCollectionDetail";
import { toast } from "react-toastify";
import QueryManagerTable from "./QueryManagerTable";
import DimensionTable from "./DimensionTable";

const EditDatabase = ({Database, setDatabase}: {Database: ReadDatabase, setDatabase: (Database: ReadDatabase) => void}) => {
    const db = useOasisBackend();
    const [editName, setEditName] = useState(Database.name);
    const [editDescription, setEditDescription] = useState(Database.description);
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState<string | null>(null);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [activeTab, setActiveTab] = useState<'entities' | 'documents' | 'queries' | 'dimensions'>('entities');
    const [editIsPublic, setEditIsPublic] = useState(Database.is_public);


    const fetchDocuments = async () => {
        try {
            const response = await db.endpoints.api.apiDatabaseDocumentsList(Database.id);
            setDocuments(response.data);
        } catch (error) {
            console.error('Error fetching documents:', error);
            toast.error("Failed to fetch documents");
        }
    };

    const deleteDocument = (documentId: string) => {
        setDocuments(documents => documents.filter(document => document.id !== documentId));
        toast.success("Document deleted successfully");
    }

    const onUploadSuccess = (document: Document) => {
        setDocuments(documents => [...documents, document]);
        toast.success("Document uploaded successfully");
    }

    useEffect(() => {
        fetchDocuments();
    }, []);


    const editDatabase = async () => {
        setEditLoading(true);
        setEditError(null);
        
        try {
            const response = await db.endpoints.api.apiDatabasesUpdate(Database.id, {
                name: editName,
                description: editDescription,
                is_public: editIsPublic
            });
            
            if (response.status === 200) {
                setDatabase({...Database, name: editName, description: editDescription, is_public: editIsPublic});
                toast.success("Database updated successfully");
            } else {
                setEditError("Failed to edit knowledge base");
                toast.error("Failed to update database");
            }
        } catch (error) {
            setEditError("Failed to edit knowledge base");
            toast.error("Failed to update database");
        } finally {
            setEditLoading(false);
        }
    }

    return (
        <div className="min-h-screen bg-gray-900 text-gray-100 p-3 sm:p-6">
            <div className="max-w-4xl mx-auto">
                <div className="mb-6 sm:mb-8">
                    <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Edit Database</h1>
                    <p className="text-gray-400 text-sm sm:text-base">Update database settings and manage content</p>
                </div>

                <div className="bg-gray-800 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
                    <h2 className="text-lg sm:text-xl font-semibold text-white mb-4">Database Settings</h2>
                    
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Database Name
                            </label>
                            <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm sm:text-base"
                                placeholder="Enter database name"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Description
                            </label>
                            <textarea
                                value={editDescription}
                                onChange={(e) => setEditDescription(e.target.value)}
                                className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-vertical text-sm sm:text-base"
                                rows={4}
                                placeholder="Enter database description"
                            />
                        </div>

                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                checked={editIsPublic}
                                onChange={(e) => setEditIsPublic(e.target.checked)}
                                className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500 focus:ring-2"
                            />
                            <label className="ml-2 text-sm font-medium text-gray-300">
                                Make this database public
                            </label>
                        </div>

                        {editError && (
                            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 sm:p-4">
                                <p className="text-red-400 text-sm">{editError}</p>
                            </div>
                        )}

                        <button 
                            onClick={editDatabase}
                            disabled={editLoading}
                            className="w-full px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 text-sm sm:text-base"
                        >
                            {editLoading ? (
                                <div className="flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Updating...
                                </div>
                            ) : (
                                "Update Database"
                            )}
                        </button>
                    </div>
                </div>

                <div className="bg-gray-800 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
                    <h2 className="text-lg sm:text-xl font-semibold text-white mb-4">Database Actions</h2>
                    <TranslateDatabaseButton DatabaseId={Database.id}/>
                </div>

                <div className="bg-gray-800 rounded-lg p-4 sm:p-6">
                    <div className="flex border-b border-gray-700 mb-4 sm:mb-6 overflow-x-auto">
                        <button
                            className={`px-4 sm:px-6 py-2 sm:py-3 font-medium transition-colors duration-200 focus:outline-none whitespace-nowrap ${
                                activeTab === 'entities' 
                                    ? 'border-b-2 border-blue-500 text-blue-400' 
                                    : 'text-gray-400 hover:text-gray-300'
                            }`}
                            onClick={() => setActiveTab('entities')}
                        >
                            Entities
                        </button>
                        <button
                            className={`px-4 sm:px-6 py-2 sm:py-3 font-medium transition-colors duration-200 focus:outline-none whitespace-nowrap ${
                                activeTab === 'queries' 
                                    ? 'border-b-2 border-blue-500 text-blue-400' 
                                    : 'text-gray-400 hover:text-gray-300'
                            }`}
                            onClick={() => setActiveTab('queries')}
                        >
                            Queries
                        </button>
                        <button
                            className={`px-4 sm:px-6 py-2 sm:py-3 font-medium transition-colors duration-200 focus:outline-none whitespace-nowrap ${
                                activeTab === 'dimensions' 
                                    ? 'border-b-2 border-blue-500 text-blue-400' 
                                    : 'text-gray-400 hover:text-gray-300'
                            }`}
                            onClick={() => setActiveTab('dimensions')}
                        >
                            Dimensions
                        </button>
                        <button
                            className={`px-4 sm:px-6 py-2 sm:py-3 font-medium transition-colors duration-200 focus:outline-none whitespace-nowrap ${
                                activeTab === 'documents' 
                                    ? 'border-b-2 border-blue-500 text-blue-400' 
                                    : 'text-gray-400 hover:text-gray-300'
                            }`}
                            onClick={() => setActiveTab('documents')}
                        >
                            Documents
                        </button>
                    </div>

                    {activeTab === 'entities' && (
                        <>
                            <div>
                                <EntityCollectionDetail databaseId={Database.id}/>
                            </div>
                        </>
                    )}

                    {activeTab === 'queries' && (
                        <div>
                            <QueryManagerTable entityCollectionId={Database.primary_entity_collection_id} databaseId={Database.id}/>
                        </div>
                    )}

                    {activeTab === 'dimensions' && (
                        <div>
                            <DimensionTable entityCollectionId={Database.primary_entity_collection_id}/>
                        </div>
                    )}

                    {activeTab === 'documents' && (
                        <div className="space-y-4 sm:space-y-6">
                            <div>
                                <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Document Management</h3>
                                <DocumentList DatabaseId={Database.id} documents={documents} onDelete={deleteDocument}/>
                            </div>
                            
                            <div className="border-t border-gray-700 pt-4 sm:pt-6">
                                <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Upload New Document</h3>
                                <DocumentUploader DatabaseId={Database.id} onUploadSuccess={onUploadSuccess}/>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default EditDatabase;